import React, { useEffect, useId, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { usePackagingOptions } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderQueries';

import { Combobox } from '~/shadcn/components/ui/Combobox';
import { Label } from '~/shadcn/components/ui/Label';

const labelClassName =
    'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground leading-tight min-h-[28px]';

/**
 * Renders a packaging SKU picker with internal data loading and loading/disabled state.
 * Mirrors VendorPicker pattern: useQuery inside component, spinner when loading, disabled when no options.
 *
 * @param {Object} props
 * @param {string} [props.value] - Selected option value (packaging model id as string).
 * @param {Function} [props.onValueChange] - Called with the selected option `{ value, label, model }` or null.
 * @param {string|React.ReactNode} [props.label='Packaging SKU'] - Label above the picker.
 * @param {boolean} [props.showLabel=true] - Whether to show the label.
 * @param {boolean} [props.enabled=true] - When false, query is not run (e.g. only when edit mode).
 * @param {string} [props.id] - Id for the combobox (defaults to generated).
 * @param {string} [props.placeholder='Select packaging SKU'] - Placeholder when not loading.
 * @returns {JSX.Element}
 */
export function PackagingPicker({
    enabled = true,
    id: idProp,
    label = 'Packaging SKU',
    onValueChange,
    placeholder = 'Select packaging SKU',
    showLabel = true,
    value = '',
}) {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const { data: options = [], error, isError, isFetching, isLoading } = usePackagingOptions({ enabled });

    useEffect(() => {
        if (isError && error) {
            toast.error(error?.message ?? 'Unable to load packaging options');
        }
    }, [isError, error]);

    const comboboxOptions = useMemo(() => options.map((opt) => ({ label: opt.label, value: opt.value })), [options]);

    const showLoading = isLoading || (isFetching && options.length === 0);

    const handleSelect = (selectedValue) => {
        if (typeof onValueChange !== 'function') return;
        if (!selectedValue) {
            onValueChange(null);
            return;
        }
        const option = options.find((opt) => opt.value === selectedValue) ?? null;
        onValueChange(option);
    };

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2" data-testid="packaging-picker">
                {showLabel && label && (
                    <Label htmlFor={id} className={labelClassName}>
                        {label}
                    </Label>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading packaging options…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2" data-testid="packaging-picker">
            {showLabel && label && (
                <Label htmlFor={id} className={labelClassName}>
                    {label}
                </Label>
            )}
            <Combobox
                id={id}
                disabled={!comboboxOptions.length || showLoading}
                options={comboboxOptions}
                value={value ?? ''}
                handleSelect={handleSelect}
                placeholder={placeholder}
                popoverWidth="trigger"
                triggerClassName="w-full justify-between"
                commandProps={{
                    filter: (val, search) => {
                        const option = options.find((opt) => opt.value === val);
                        if (!option) return false;
                        return option.label.toLowerCase().includes(search.toLowerCase());
                    },
                }}
            />
        </div>
    );
}

PackagingPicker.displayName = 'PackagingPicker';
